import { useMemo, useState } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { numberFormatter } from "../../helper/Reports/common";
import StickyInsightsTable from "../../components/Tables/StickyInsightsTable";
import {
  isCampaignTypeSP,
  NON_SORTABLE_COLUMN_KEYS,
  SB_SD_OPTIMISATION_TOOLTIP,
} from "./adjustBidLocalFilters";

function isColumnSortable(column) {
  return !!column?.key && !NON_SORTABLE_COLUMN_KEYS.includes(column.key);
}

function getAlign(column) {
  return column?.isDimension || NON_SORTABLE_COLUMN_KEYS.includes(column?.key)
    ? "left"
    : column?.alignment || "right";
}

export default function AdjustBidV2Table({
  columns = [],
  rows = [],
  isLoading = false,
  queryOrder = [["spends", "desc"]],
  onSortClick,
  groupByOptionsData = [],
  extraGroupByOptions = [],
  onExtraGroupByChange = () => {},
  showGroupByPlusIcon = true,
  summaryData = {},
  onToggleRow,
  onToggleAll,
  noDataFoundMessage = "No Data Found",
}) {
  const [groupByPlusAnchorEl, setGroupByPlusAnchorEl] = useState(null);

  const lastDimensionColumnKey = useMemo(() => {
    const dimensionColumns = columns.filter((column) => column?.isDimension);
    return dimensionColumns[dimensionColumns.length - 1]?.key ?? null;
  }, [columns]);

  const defaultDimensionKeys = useMemo(() => {
    const defaultOption = groupByOptionsData?.find(
      (option) => option?.value === "campaign_placement"
    );
    return (
      defaultOption?.dimensions ?? [
        "campaign_id",
        "campaign_name",
        "placement_name",
      ]
    );
  }, [groupByOptionsData]);

  const renderDimensionCell = (column, row) => {
    const { productName, asin } = column?.innerKeys || {};
    if (productName) {
      const name = row?.[productName] ?? row?.[column.key];
      const asinValue = asin?.value ? row?.[asin.value] : null;
      const displayName = name ?? "-";
      return (
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={displayName} arrow placement="top">
            <Typography
              sx={{
                fontSize: "12px",
                color: "#333",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: column.minWidth || 220,
              }}
            >
              {displayName}
            </Typography>
          </Tooltip>
          {asinValue ? (
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              {asin?.label ? `${asin.label}: ` : ""}
              {asinValue}
            </Typography>
          ) : null}
        </Box>
      );
    }

    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Tooltip title={String(value)} arrow placement="top">
        <Typography
          sx={{
            fontSize: "12px",
            color: "#333",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: column.minWidth || 160,
          }}
        >
          {String(value)}
        </Typography>
      </Tooltip>
    );
  };

  const renderBidCell = (row) => {
    if (!isCampaignTypeSP(row?.campaign_type)) {
      return (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>-</Typography>
      );
    }
    const value = row?.bid ?? row?.bid_adjustment_value;
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(value, false, "percent", 2);
  };

  const renderCell = (column, row) => {
    if (column.key === "bid_adjustment_value" || column.key === "bid") {
      return renderBidCell(row);
    }
    if (column.key === "campaign_bid_strategy") {
      return row?.campaign_bid_strategy ?? "-";
    }
    if (column?.isDimension) return renderDimensionCell(column, row);

    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(
      value,
      !!column?.isCurrencyFormat,
      column?.format,
      column?.maximumFractionDigits
    );
  };

  const renderSummaryValue = (column) => {
    if (column?.isDimension || NON_SORTABLE_COLUMN_KEYS.includes(column.key)) {
      return "-";
    }
    if (column.key === "campaign_bid_strategy") {
      return summaryData?.[column.key] ?? "-";
    }
    if (column.key === "bid_adjustment_value" || column.key === "bid") {
      const bidSummary = summaryData?.[column.key];
      if (bidSummary === undefined || bidSummary === null || bidSummary === "") {
        return "-";
      }
      return numberFormatter(bidSummary, false, "percent", 2);
    }
    const value = summaryData?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(
      value,
      !!column?.isCurrencyFormat,
      column?.format,
      column?.maximumFractionDigits
    );
  };

  const renderHeaderContent = (column) => {
    const canRemoveDimension =
      column?.isDimension &&
      !defaultDimensionKeys.includes(column.key) &&
      column.key !== "campaign_id";

    const showPlus =
      column.key === lastDimensionColumnKey &&
      showGroupByPlusIcon &&
      extraGroupByOptions.length > 0;

    if (!showPlus && !canRemoveDimension) return null;

    return (
      <>
        {showPlus ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setGroupByPlusAnchorEl(e.currentTarget);
            }}
            sx={{
              width: 22,
              height: 22,
              backgroundColor: "#7558ea",
              color: "white",
              "&:hover": { backgroundColor: "#5a42c7" },
              p: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
        {canRemoveDimension ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              const dimensions =
                groupByOptionsData.find((option) =>
                  option?.dimensions?.includes(column.key)
                )?.dimensions || [column.key];
              onExtraGroupByChange?.(dimensions, "remove");
            }}
            sx={{ width: 22, height: 22, p: 0 }}
          >
            <CancelIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
      </>
    );
  };

  return (
    <StickyInsightsTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      queryOrder={queryOrder}
      summaryData={summaryData}
      onSortClick={onSortClick}
      onToggleRow={onToggleRow}
      onToggleAll={onToggleAll}
      noDataFoundMessage={noDataFoundMessage}
      isRowSelectable={(row) =>
        isCampaignTypeSP(row?.campaign_type) && !row?.isDisabled
      }
      getDisabledCheckboxTooltip={() => SB_SD_OPTIMISATION_TOOLTIP}
      isColumnSortable={isColumnSortable}
      getAlign={getAlign}
      getRowKey={(row, index) =>
        `${row?.campaign_id}-${row?.placement_name}-${index}`
      }
      renderCell={renderCell}
      renderSummaryValue={renderSummaryValue}
      renderHeaderContent={renderHeaderContent}
      footer={
        <Menu
          anchorEl={groupByPlusAnchorEl}
          open={Boolean(groupByPlusAnchorEl)}
          onClose={() => setGroupByPlusAnchorEl(null)}
          disableScrollLock
        >
          {extraGroupByOptions.map((option) => (
            <MenuItem
              key={option.value}
              sx={{ fontSize: "12px" }}
              onClick={() => {
                onExtraGroupByChange?.(option.dimensions, "add");
                setGroupByPlusAnchorEl(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      }
    />
  );
}
